import React, { useState } from 'react';
import { Play, Info, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const Hero = ({ featuredContent, onMovieClick }) => {
  const { isSidebarOpen, toggleSidebar, currentTheme } = useTheme();
  const { t } = useLanguage();
  const [isMuted, setIsMuted] = useState(true);

  if (!featuredContent) return null;

  const handlePlayClick = () => {
    if (['fabiomeyer', 'jemmyx'].includes(currentTheme)) {
      toggleSidebar();
    } else {
      onMovieClick(featuredContent);
    }
  };

  const handleMoreInfoClick = () => {
    onMovieClick(featuredContent);
  };

  // Helper function to get the correct image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    // If it's already a full URL (starts with http), use it directly
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    // Otherwise, use TMDB prefix
    return `images/${imagePath}`;
  };

  return (
  <div className="relative h-screen overflow-hidden pb-16">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${getImageUrl(featuredContent.backdrop_path)})`,
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-12 max-w-2xl">
        {/* Title */}
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg">
          {featuredContent.title || featuredContent.name}
        </h1>

        {/* Overview (plain text, no HTML tags) */}
        <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed line-clamp-3">
          {featuredContent.overview ? featuredContent.overview.replace(/<[^>]+>/g, '') : ''}
        </p>

        {/* Buttons */}
        <div className="flex space-x-4 mb-8">
          <Button 
            className="bg-white hover:bg-white/80 text-black font-semibold px-8 py-3 text-lg flex items-center space-x-2 transition-all"
            onClick={handlePlayClick}
          >
            <Play className="w-6 h-6 fill-current" />
            <span>{t('play')}</span>
          </Button>
          
          <Button 
            variant="secondary" 
            className="bg-gray-500/70 hover:bg-gray-500/50 text-white font-semibold px-8 py-3 text-lg flex items-center space-x-2 backdrop-blur-sm transition-all"
            onClick={handleMoreInfoClick}
          >
            <Info className="w-6 h-6" />
            <span>{t('moreInfo')}</span>
          </Button>
        </div>

        {/* Movie Info */}
        <div className="flex items-center space-x-4 text-white/80">
          <span className="text-lg font-semibold">
            {featuredContent.vote_average?.toFixed(1)} ★
          </span>
          {featuredContent.release_date || featuredContent.first_air_date ? (
            <span className="text-lg">
              {new Date(featuredContent.release_date || featuredContent.first_air_date).toLocaleDateString()}
            </span>
          ) : null}
          {featuredContent.runtime && (
            <span className="text-lg">{featuredContent.runtime}m</span>
          )}
        </div>
      </div>

      {/* Audio Control */}
      <div className="absolute bottom-24 right-6 z-10">
        <Button
          variant="ghost"
          size="sm"
          className="p-3 rounded-full border border-white/20 bg-black/30 backdrop-blur-sm text-white hover:bg-white/10"
          onClick={() => setIsMuted(!isMuted)}
        >
          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </Button>
      </div>
    </div>
  );
};

export default Hero;